/**
 * Edit a creative's brief (docs/BRIEF-EDIT-LOCK.md).
 *
 * Aziz, 2026-09-24: the brief belongs to its content creator while the work is still
 * with them. Once the next person's work starts it locks, and only "Send back to
 * brief" reopens it. The owner and a manager can always edit, with a reason.
 *
 * The server decides all of that (api/app/edit_lock.py); this dialog only shows it.
 * It sends the fields that changed, what the editor saw of them (so two people
 * editing at once get a 409 rather than silently overwriting each other), and the
 * reason when one is needed.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, Pencil, Undo2, X } from 'lucide-react';
import { toast } from 'sonner';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { CHANNELS_DEFAULT, AD_PLATFORMS } from '@shared/enums';
import { cn } from '@/lib/cn';
import {
  qk, fetchClient, editCreative, sendBackToBrief, type Creative, type EditLock,
} from '@/lib/queries';
import RichEditor from '@/components/ui/RichEditor';
import { htmlToPlain } from '@/components/ui/RichView';

type Form = {
  title: string; description: string; description_html: string; taxonomy_primary: string; taxonomy_secondary: string;
  talent_id: string; channels: string[]; is_paid: boolean; ad_platforms: string[];
  execution_date: string; posting_date: string; reference_urls: string;
};

const fromCreative = (c: Creative): Form => ({
  title: c.title ?? '', description: c.description ?? '', description_html: c.description_html ?? '',
  taxonomy_primary: c.taxonomy_primary ?? '', taxonomy_secondary: c.taxonomy_secondary ?? '',
  talent_id: c.talent_id ?? '', channels: [...(c.channels ?? [])], is_paid: !!c.is_paid,
  ad_platforms: [...(c.ad_platforms ?? [])], execution_date: c.execution_date ?? '',
  posting_date: c.posting_date ?? '', reference_urls: (c.reference_urls ?? []).join('\n'),
});

const same = (a: unknown, b: unknown) => {
  if (Array.isArray(a) && Array.isArray(b)) return [...a].sort().join('|') === [...b].sort().join('|');
  return a === b;
};

const detail = (e: unknown) => {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && 'message' in d) return String((d as { message: string }).message);
  return 'Could not save';
};

export default function EditCreativeDialog({ creative, lock, open, onClose }: {
  creative: Creative; lock: EditLock | undefined; open: boolean; onClose: () => void;
}) {
  const qc = useQueryClient();
  const client = useQuery({ queryKey: qk.client(creative.client_id), queryFn: () => fetchClient(creative.client_id), enabled: open });
  const seen = useMemo(() => fromCreative(creative), [creative]);
  const [f, setF] = useState<Form>(seen);
  const [reason, setReason] = useState('');
  const [sendingBack, setSendingBack] = useState(false);
  const [backReason, setBackReason] = useState('');
  // Shoot or existing material: a production choice, not part of the brief, so the
  // brief lock does not hold it (Aziz, 2026-09-24: "make the switch editable").
  // a design piece can need a photoshoot too (Aziz, 2026-10-04)
  const filmable = ['video', 'reel', 'photo', 'static', 'carousel', 'story'].includes(creative.creative_type);
  const seenShoot = creative.needs_shoot !== false;
  const [needsShoot, setNeedsShoot] = useState(seenShoot);
  useEffect(() => { if (open) { setF(fromCreative(creative)); setNeedsShoot(creative.needs_shoot !== false); setReason(''); setSendingBack(false); setBackReason(''); } }, [open]);   // eslint-disable-line react-hooks/exhaustive-deps

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const toggle = (k: 'channels' | 'ad_platforms', v: string) =>
    setF((x) => ({ ...x, [k]: x[k].includes(v) ? x[k].filter((y) => y !== v) : [...x[k], v] }));

  const refresh = () => {
    qc.invalidateQueries({ queryKey: qk.creative(creative.id) });
    qc.invalidateQueries({ queryKey: qk.creativeActivity(creative.id) });
    qc.invalidateQueries({ queryKey: ['editLock', creative.id] });
    qc.invalidateQueries({ queryKey: ['track', creative.id] });
    qc.invalidateQueries({ queryKey: ['shoot', creative.id] });
    qc.invalidateQueries({ queryKey: ['creatives'] });
  };

  const changed = (Object.keys(f) as (keyof Form)[]).filter((k) => !same(f[k], seen[k]));
  const onlyPosting = !!lock?.posting_date_only && !lock?.override;
  const canEdit = !!lock && (lock.editable || lock.override || lock.posting_date_only);
  const shootChanged = filmable && needsShoot !== seenShoot;
  const needsReason = !!lock?.locked && !!lock?.override && changed.length > 0;
  const total = changed.length + (shootChanged ? 1 : 0);
  const reasonOk = reason.replace(/\s+/g, ' ').trim().length >= 10;

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      const expected: Record<string, unknown> = {};
      for (const k of changed) {
        const v = k === 'reference_urls' ? f.reference_urls.split(/\s+/).map((u) => u.trim()).filter(Boolean) : f[k];
        body[k] = v;
        expected[k] = k === 'reference_urls' ? creative.reference_urls ?? [] : (creative as unknown as Record<string, unknown>)[k] ?? null;
      }
      if (shootChanged) body.needs_shoot = needsShoot;
      if (needsReason) body.reason = reason.trim();
      return editCreative(creative.id, { ...body, expected });
    },
    onSuccess: () => { toast.success('Saved'); refresh(); onClose(); },
    onError: (e) => { toast.error(detail(e)); refresh(); },
  });

  const back = useMutation({
    mutationFn: () => sendBackToBrief(creative.id, backReason.trim()),
    onSuccess: () => { toast.success('Sent back to the brief. It is open again.'); refresh(); setSendingBack(false); },
    onError: (e) => toast.error(detail(e)),
  });

  if (!open) return null;
  const tax = client.data?.taxonomy;
  const talents = client.data?.talents ?? [];
  const off = (k: keyof Form) => !canEdit || (onlyPosting && k !== 'posting_date') || (k === 'execution_date' && !!lock?.execution_date_from_shoot);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-creative-dialog">
      <div className="bg-bg-card border border-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-card">
        <div className="flex items-center justify-between p-5 border-b border-slate-800">
          <div className="flex items-start gap-3">
            <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
              lock?.locked ? 'bg-amber-500/15 text-amber-300' : 'bg-accent-600/20 text-accent-400')}>
              {lock?.locked ? <Lock size={18} /> : <Pencil size={18} />}
            </div>
            <div>
              <h3 className="font-medium text-muted-strong">Edit {creative.code}</h3>
              <p className="text-xs text-muted">The brief: what it is, for whom, where it goes. The script has its own card.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted hover:text-muted-strong" aria-label="Close"><X size={20} /></button>
        </div>

        {lock?.locked && (
          <div className="mx-5 mt-4 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2.5 text-xs" data-testid="edit-lock-banner">
            <div className="text-amber-200">{lock.message}</div>
            {lock.override && <div className="mt-1 text-muted">As owner or manager you can still change it. Say why below; it goes in the log and the content creator is told.</div>}
            {onlyPosting && <div className="mt-1 text-muted">At the posting step you can move the posting date.</div>}
            {!lock.override && !onlyPosting && lock.owners.length > 0 && <div className="mt-1 text-muted">The brief belongs to {lock.owners.join(' and ')}.</div>}
            {lock.can_send_back && !sendingBack && (
              <button type="button" onClick={() => setSendingBack(true)} className="mt-2 inline-flex items-center gap-1 text-accent-400 hover:underline" data-testid="send-back-open">
                <Undo2 size={12} /> Send back to the brief
              </button>
            )}
            {sendingBack && (
              <div className="mt-2 space-y-2">
                <textarea value={backReason} onChange={(e) => setBackReason(e.target.value)} rows={2} dir="auto" maxLength={500}
                  placeholder="Why it goes back, e.g. the clinic changed the offer"
                  className="w-full rounded-lg border border-slate-700 bg-bg px-3 py-2 text-xs text-muted-strong focus:outline-none focus:border-accent-400"
                  data-testid="send-back-reason" />
                <div className="flex gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setSendingBack(false)}>Cancel</Button>
                  <Button size="sm" onClick={() => back.mutate()} loading={back.isPending}
                    disabled={backReason.replace(/\s+/g, ' ').trim().length < 10} data-testid="send-back-confirm">
                    Send back and reopen
                  </Button>
                </div>
                <p className="text-[11px] text-muted">Whoever is working on it now is told. It locks again when their work starts again.</p>
              </div>
            )}
          </div>
        )}
        {!lock?.locked && lock && !lock.editable && (
          <div className="mx-5 mt-4 rounded-lg border border-slate-700 px-3 py-2.5 text-xs text-muted" data-testid="edit-lock-banner">
            The brief belongs to {lock.owners.join(' and ') || 'its content creator'}. Ask them to change it.
          </div>
        )}

        {filmable && (
          <div className="px-5 pt-4 space-y-1.5" data-testid="edit-needs-shoot">
            <div className="text-xs text-muted">How it is made</div>
            <div className="inline-flex rounded-lg border border-slate-700 p-0.5">
              {([[true, 'Needs a shoot'], [false, 'Existing material']] as const).map(([v, label]) => (
                <button key={label} type="button" onClick={() => setNeedsShoot(v)} data-testid={v ? 'edit-shoot-yes' : 'edit-shoot-no'}
                  className={cn('px-3 py-1.5 text-xs rounded-md transition',
                    needsShoot === v ? 'bg-accent-600/25 text-accent-300' : 'text-muted hover:text-muted-strong')}>
                  {label}
                </button>
              ))}
            </div>
            {shootChanged && (
              <p className="text-[11px] text-muted" data-testid="edit-needs-shoot-note">
                {needsShoot
                  ? 'It goes back to Shoot, as long as the editor has not started. A waiting editor task is cancelled and handed out again when the raws are in.'
                  : lock?.execution_date_from_shoot
                    ? 'It is on a shoot session. Take it off the session on the shoot card, or it stays on it.'
                    : 'No shoot, no raws: once the script is approved it goes straight to the editor.'}
              </p>
            )}
          </div>
        )}

        <fieldset className="p-5 space-y-4 disabled:opacity-60" disabled={!canEdit}>
          <Input label="Title" value={f.title} onChange={(e) => set('title', e.target.value)} disabled={off('title')} data-testid="edit-title" />
          <div className="flex flex-col gap-1.5 text-sm">
            <span className="text-xs text-muted">Brief / concept</span>
            <RichEditor html={f.description_html} plain={f.description} disabled={off('description') || !canEdit} minHeight={100} testid="edit-description"
              onChange={(h) => { set('description_html', h); set('description', htmlToPlain(h)); }} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select label={tax?.primary.label ?? 'Primary tag'} value={f.taxonomy_primary} onChange={(v) => set('taxonomy_primary', v)} disabled={off('taxonomy_primary')}
              options={[{ value: '', label: '(any)' }, ...(tax?.primary.values ?? []).map((v) => ({ value: v, label: v })),
                ...(f.taxonomy_primary && !(tax?.primary.values ?? []).includes(f.taxonomy_primary) ? [{ value: f.taxonomy_primary, label: f.taxonomy_primary }] : [])]} />
            <Select label={tax?.secondary.label ?? 'Secondary tag'} value={f.taxonomy_secondary} onChange={(v) => set('taxonomy_secondary', v)} disabled={off('taxonomy_secondary')}
              options={[{ value: '', label: '(any)' }, ...(tax?.secondary.values ?? []).map((v) => ({ value: v, label: v })),
                ...(f.taxonomy_secondary && !(tax?.secondary.values ?? []).includes(f.taxonomy_secondary) ? [{ value: f.taxonomy_secondary, label: f.taxonomy_secondary }] : [])]} />
          </div>
          {(tax?.talent.enabled || f.talent_id) && (
            <div>
              <Select label={tax?.talent.label ?? 'Doctor'} value={f.talent_id} onChange={(v) => set('talent_id', v)} searchable disabled={off('talent_id')}
                options={[{ value: '', label: '(none)' }, ...talents.map((t) => ({ value: t.id, label: t.name })),
                  ...(f.talent_id && !talents.some((t) => t.id === f.talent_id) ? [{ value: f.talent_id, label: `${creative.talent_name ?? 'Current'} (not in the clinic list)` }] : [])]} />
              {f.talent_id !== seen.talent_id && ['sent', 'approved', 'changes_requested'].includes(creative.script_status ?? '') && (
                <p className="mt-1 text-[11px] text-amber-300" data-testid="doctor-reset-note">The script goes back to draft: the new doctor has not seen it.</p>
              )}
            </div>
          )}
          <Chips label="Channels" options={CHANNELS_DEFAULT as unknown as string[]} value={f.channels} onToggle={(v) => toggle('channels', v)} disabled={off('channels')} />
          <label className="flex items-center gap-2 text-sm text-muted-strong">
            <input type="checkbox" checked={f.is_paid} onChange={(e) => set('is_paid', e.target.checked)} disabled={off('is_paid')} className="accent-accent-400" />
            Paid distribution
          </label>
          {f.is_paid && <Chips label="Ad platforms" options={AD_PLATFORMS as unknown as string[]} value={f.ad_platforms} onToggle={(v) => toggle('ad_platforms', v)} disabled={off('ad_platforms')} />}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Input label="Execution date (shoot/design)" type="date" value={f.execution_date} onChange={(e) => set('execution_date', e.target.value)} disabled={off('execution_date')} />
              {lock?.execution_date_from_shoot && <p className="mt-1 text-[11px] text-muted">Set by the shoot session. Move the session to change it.</p>}
            </div>
            <Input label="Posting date" type="date" value={f.posting_date} onChange={(e) => set('posting_date', e.target.value)} disabled={off('posting_date')} data-testid="edit-posting-date" />
          </div>
          <label className="flex flex-col gap-1.5 text-sm">
            <span className="text-xs text-muted">Reference links, one per line</span>
            <textarea value={f.reference_urls} onChange={(e) => set('reference_urls', e.target.value)} rows={3} disabled={off('reference_urls')}
              className="rounded-lg border border-slate-800 bg-bg px-3 py-2 text-xs text-muted-strong focus:outline-none focus:border-accent-400 disabled:opacity-60" />
          </label>
          {needsReason && (
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="text-xs text-amber-300">Why it changes now (at least 10 characters)</span>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} dir="auto" maxLength={500}
                placeholder="e.g. the clinic renamed the campaign"
                className="rounded-lg border border-amber-500/40 bg-bg px-3 py-2 text-sm text-muted-strong focus:outline-none focus:border-amber-400"
                data-testid="edit-reason" />
            </label>
          )}
        </fieldset>

        <div className="flex items-center justify-between gap-2 p-5 border-t border-slate-800">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {(canEdit || shootChanged) && (
            <Button onClick={() => save.mutate()} loading={save.isPending}
              disabled={!total || !f.title.trim() || (needsReason && !reasonOk)} data-testid="edit-save">
              {total ? `Save ${total} change${total === 1 ? '' : 's'}` : 'Nothing changed'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Chips({ label, options, value, onToggle, disabled }: {
  label: string; options: string[]; value: string[]; onToggle: (v: string) => void; disabled?: boolean;
}) {
  const all = [...options, ...value.filter((v) => !options.includes(v))];
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-muted">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {all.map((o) => (
          <button key={o} type="button" disabled={disabled} onClick={() => onToggle(o)}
            className={cn('text-xs px-2.5 py-1 rounded-full border transition disabled:opacity-60',
              value.includes(o) ? 'border-accent-400 bg-accent-600/20 text-accent-300' : 'border-slate-700 text-muted hover:border-slate-500')}>
            {o.replace(/_/g, ' ')}
          </button>
        ))}
      </div>
    </div>
  );
}
